import { answerQuestions, useCountryList } from "@/hooks/useRequest/useSurveyRequest";
import { AnswerQuestionModel } from "@/interfaces/surveyTypes";
import log2sensors, { SensorsEventIds } from "@/utils/log2sensors";
import { useTranslation } from "next-i18next";
import { useEffect, useState } from "react";
import SurveyTitle from "../SurveyTitleV2";
import SurveyFooter from "../SurveyFooter";
import SelectOptions from "@/components/SelectOptions";
import ContentContainer from "@/containers/common/ContentContainer";
import NoticeModalBottom from "@/containers/common/NoticeModalBottom";
import useInApp from "@/hooks/useIsApp";
import bridge from "@/utils/dsBridge";
import { BridgeCallHandlers } from "@/interfaces/enums";
import { useRouter } from "next/router";

interface TaxResidentProps {
	answerFieldToValueMap: Map<string, string>;
	questionFieldToIdMap: Map<string, string>;
	nationality: string;
	onNextClick: () => void;
}

export default function TaxResident(props: TaxResidentProps) {
	const { t } = useTranslation("survey");
	const [loading, setLoading] = useState(false);
	const [selectedType, setSelectedType] = useState<string>();
	const [visible, setVisible] = useState(false);
	const isInApp = useInApp();
	const router = useRouter();
	const { countryList } = useCountryList({ language: router.locale });

	useEffect(() => {
		const _answerType = props.answerFieldToValueMap.get("notChinaTaxResident") || "";
		if (!_answerType) return;

		setSelectedType(_answerType);
	}, [props.answerFieldToValueMap]);

	const handleConfirm = async (_selectedType: string) => {
		try {
			if (!_selectedType) return;
			const questionId = props.questionFieldToIdMap.get("notChinaTaxResident");
			if (!questionId) {
				throw Error("missing notChinaTaxResident questionId");
			}

			let answer = _selectedType.toString();
			if (props.nationality === "CN") {
				// 中国大陆税务 是 === 我不是中国大陆税收居民
				answer = _selectedType.toString() === "1" ? "0" : "1";
			}

			const payload: AnswerQuestionModel[] = [
				{
					answer,
					questionId: questionId,
				},
			];

			setLoading(true);
			await answerQuestions({ payload });
			props.onNextClick();
		} catch (error) {
			console.log(error);
		} finally {
			setLoading(false);
			log2sensors(SensorsEventIds.AO_TAX_RESIDENT_NEXTPAGE);
		}
	};

	useEffect(() => {
		log2sensors(SensorsEventIds.AO_TAX_RESIDENT_VIEW);
	}, []);

	const openCustomService = () => {
		if (isInApp) {
			bridge.call(BridgeCallHandlers.ON_CUSTOMER_SERVICE_BUTTON_CLICK);
			return;
		}
		window.open(`${window.location.origin}/${router.locale}/contact-us`, "_blank");
	};

	const renderNationality = () => {
		if (countryList.length === 0) return props.nationality;
		return countryList.find((item) => item.value === props.nationality)?.label || props.nationality;
	};

	return (
		<ContentContainer
			animation
			header={
				<div className="px-4.5">
					<SurveyTitle
						title={t("are-you-a-tax-resident-of-nationality", { nationality: renderNationality() })}
						subtitle={t("most-users-select-yes-here")}
					/>
				</div>
			}
			footer={
				<div className="px-4.5">
					<SurveyFooter nextBtn={false} />
				</div>
			}
		>
			<div className="h-12" />
			<SelectOptions
				value={selectedType}
				options={[
					{ label: t("yes", { ns: "common" }), value: "1" },
					{ label: t("no", { ns: "common" }), value: "0" },
				]}
				loading={loading}
				onChange={(val) => {
					if (props.nationality !== "CN" && val === "0") {
						setVisible(true);
						return;
					}

					setSelectedType(String(val));
					handleConfirm(String(val));
				}}
			/>

			<NoticeModalBottom
				visible={visible}
				title=""
				content={t("if-your-nationality-and-tax-not-match-desc")}
				onClose={() => setVisible(false)}
				okText={t("if-your-nationality-and-tax-not-match-btn")}
				onOk={() => {
					setVisible(false);
					openCustomService();
				}}
			/>
		</ContentContainer>
	);
}
